import { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { FileText } from 'lucide-react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useDataProvider, useNotify } from 'react-admin';

export interface SpecificationItem {
  parameter: string;
  // 标准值和误差由用户输入
  standardValue: number | null;
  error: number | null;
  // 最小值和最大值根据标准值与误差计算
  minValue: number | null;
  maxValue: number | null;
  unit: string;
}

export interface Specification {
  id: string;
  // 物料名称与编码（编码对应 materials.productId）
  materialName: string;
  materialCode: string;
  items: SpecificationItem[];
  createdAt: string;
}

interface SpecDataManagementPageProps {
  specifications: Specification[];
  onUpdate: (specs: Specification[]) => void;
}

export default function SpecDataManagementPage({ specifications = [], onUpdate }: SpecDataManagementPageProps) {
  const API_URL = process.env.REACT_APP_API_URL || '/api';
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Omit<Specification, 'id' | 'createdAt'>>({
    materialName: '',
    materialCode: '',
    items: []
  });
  const [newItem, setNewItem] = useState<SpecificationItem>({
    parameter: '',
    standardValue: null,
    error: null,
    minValue: null,
    maxValue: null,
    unit: ''
  });

  const dataProvider = useDataProvider();
  const [materials, setMaterials] = useState<any[]>([]);
  // server-driven specs list and pagination
  const [specs, setSpecs] = useState<Specification[]>(specifications || []);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loadingList, setLoadingList] = useState(false);
  const notify = useNotify();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // 拉取 materials 列表供下拉使用（尽量一次性拉取较多条目）
    dataProvider.getList('materials', {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: 'productId', order: 'ASC' },
      filter: {},
    }).then(res => {
      setMaterials(res.data || []);
    }).catch(() => {
      setMaterials([]);
    });
  }, [dataProvider]);

  // fetch specifications from server with pagination
  const fetchSpecs = useCallback(async (p: number, pp: number, filterObj: any = null) => {
    setLoadingList(true);
    try {
      const start = (p - 1) * pp;
      const end = start + pp - 1;
      const params = new URLSearchParams();
      params.set('range', `[${start},${end}]`);
      params.set('perPage', String(pp));
      if (filterObj) params.set('filter', JSON.stringify(filterObj));

      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/specifications?${params.toString()}`, {
        method: 'GET',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const list = await res.json();
      const contentRange = res.headers.get('Content-Range') || '';
      // header like: "specifications start-end/total"
      const m = /\/(\d+)$/.exec(contentRange);
      const total = m ? parseInt(m[1], 10) : list.length;

      setSpecs(list || []);
      setTotalCount(total);
      setPage(p);
      setPerPage(pp);
    } catch (err) {
      console.error('Failed to load specs', err);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    fetchSpecs(page, perPage);
  }, [fetchSpecs, page, perPage]);

  const handleAddItem = () => {
    if (newItem.parameter && newItem.unit) {
      // 计算最小/最大值
      const std = newItem.standardValue;
      const err = newItem.error;
      const min = (std != null && err != null) ? Number((std - err).toFixed(6)) : (newItem.minValue ?? null);
      const max = (std != null && err != null) ? Number((std + err).toFixed(6)) : (newItem.maxValue ?? null);

      const itemToAdd: SpecificationItem = {
        parameter: newItem.parameter,
        standardValue: newItem.standardValue,
        error: newItem.error,
        minValue: min,
        maxValue: max,
        unit: newItem.unit,
      };

      setFormData({
        ...formData,
        items: [...formData.items, itemToAdd]
      });
      setNewItem({
        parameter: '',
        standardValue: null,
        error: null,
        minValue: null,
        maxValue: null,
        unit: ''
      });
    }
  };

  const handleRemoveItem = (index: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const handleSave = useCallback(async () => {
    if (!(formData.materialName && formData.materialCode && formData.items.length > 0)) {
      notify('Please provide material info and add at least one test item', { type: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const dto = {
        MaterialName: formData.materialName,
        MaterialCode: formData.materialCode,
        Items: formData.items.map(i => ({
          Parameter: i.parameter,
          StandardValue: i.standardValue ?? null,
          Error: i.error ?? null,
          MinValue: i.minValue ?? null,
          MaxValue: i.maxValue ?? null,
          Unit: i.unit ?? ''
        }))
      };

      const token = localStorage.getItem('token');

      let res: Response;
      if (editingId) {
        res = await fetch(`${API_URL}/specifications/${editingId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify(dto)
        });
      } else {
        res = await fetch(`${API_URL}/specifications`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
          },
          body: JSON.stringify(dto)
        });
      }

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }

      const returned = await res.json().catch(() => null);

      if (editingId) {
        if (typeof onUpdate === 'function') {
          onUpdate(specifications.map(s => s.id === editingId ? { ...(returned ?? { id: editingId }), ...formData } : s));
        }
        setEditingId(null);
      } else {
        const newSpec: Specification = {
          id: (returned && returned.id) ? String(returned.id) : Date.now().toString(),
          ...formData,
          createdAt: (returned && returned.createdAt) ? returned.createdAt : new Date().toISOString()
        };
        if (typeof onUpdate === 'function') {
          onUpdate([...specifications, newSpec]);
        }
      }

      notify('Save successful', { type: 'success' });
      // refresh list on success
      try {
        fetchSpecs(page, perPage);
      } catch (e) {
        // ignore
      }
      setIsAdding(false);
      setFormData({ materialName: '', materialCode: '', items: [] });
    } catch (error: any) {
      console.error('Save spec error', error);
      notify(`Save failed: ${error?.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  }, [formData, editingId, specifications, onUpdate, notify, fetchSpecs, page, perPage]);

  const handleEdit = (spec: Specification) => {
    setEditingId(spec.id);
    // 确保 items 包含 standardValue 与 error 字段（兼容旧数据仅有 min/max）
    const normalizedItems = spec.items.map((it: any) => {
      const standardValue = it.standardValue != null ? it.standardValue : (
        it.minValue != null && it.maxValue != null ? (Number(it.minValue) + Number(it.maxValue)) / 2 : null
      );
      const error = it.error != null ? it.error : (
        it.minValue != null && it.maxValue != null ? Math.abs(Number(it.maxValue) - Number(it.minValue)) / 2 : null
      );
      return {
        parameter: it.parameter,
        standardValue: standardValue,
        error: error,
        minValue: it.minValue ?? null,
        maxValue: it.maxValue ?? null,
        unit: it.unit ?? ''
      } as SpecificationItem;
    });

    setFormData({
      materialName: spec.materialName,
      materialCode: spec.materialCode,
      items: normalizedItems
    });
    setIsAdding(true);
  };

  const handleDelete = (id: string) => {
    (async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_URL}/specifications/${id}`, {
            method: 'DELETE',
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            }
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          // Refresh server-driven list so paging state stays consistent.
          const nextTotal = Math.max(0, totalCount - 1);
          const nextTotalPages = Math.max(1, Math.ceil(nextTotal / perPage));
          const targetPage = Math.min(page, nextTotalPages);
          if (targetPage !== page) {
            setPage(targetPage);
          } else {
            fetchSpecs(targetPage, perPage);
          }
          if (typeof onUpdate === 'function') onUpdate(specifications.filter(spec => spec.id !== id));
          notify('Delete successful', { type: 'success' });
        } catch (err: any) {
          console.error('Delete spec error', err);
          notify(`Delete failed: ${err?.message || 'Unknown error'}`, { type: 'error' });
        }
    })();
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData({
      materialName: '',
      materialCode: '',
      items: []
    });
  };

  return (
    <div className="space-y-6" style={{ margin: 20 }}>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl mb-1">Specification Data Management</h2>
          <p className="text-sm text-gray-500">Manage quality standards and specifications for raw materials</p>
        </div>
        {!isAdding && (
          <Button onClick={() => setIsAdding(true)} className="bg-blue-600 hover:bg-blue-700 text-white">
            <Plus className="size-4 mr-2" />
            New Specification
          </Button>
        )}
      </div>

      {isAdding && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 space-y-6">
          <div className="flex items-center justify-between pb-4 border-b">
            <h3 className="text-xl">{editingId ? 'Edit Specification' : 'New Specification'}</h3>
            <span className="text-sm text-gray-500">Fill in material specification information</span>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <Label htmlFor="materialName" className="text-sm text-gray-700 whitespace-nowrap">Material</Label>
              <Autocomplete
                options={materials}
                getOptionLabel={(opt: any) => `${opt.productId ?? opt.id} - ${opt.name ?? opt.productName ?? ''}`}
                value={materials.find(m => String(m.productId ?? m.id) === String(formData.materialCode)) || null}
                onChange={(_, value: any | null) => {
                  if (value) {
                    setFormData({ ...formData, materialName: value.name || value.productName || '', materialCode: String(value.productId ?? value.id) });
                  } else {
                    setFormData({ ...formData, materialName: '', materialCode: '' });
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select material (from materials list)" className="mt-1.5" />
                )}
                isOptionEqualToValue={(option, val) => String(option.productId ?? option.id) === String(val?.productId ?? val?.id ?? val)}
              />
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-base text-gray-700 pt-2">Test Items</h4>
            
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="grid grid-cols-5 gap-3 items-end">
                <div>
                  <Label htmlFor="parameter" className="text-xs text-gray-600">Parameter</Label>
                  <Input
                    id="parameter"
                    value={newItem.parameter}
                    onChange={(e) => setNewItem({ ...newItem, parameter: e.target.value })}
                    placeholder="e.g. Purity"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="standardValue" className="text-xs text-gray-600">Standard</Label>
                  <Input
                    id="standardValue"
                    type="number"
                    value={newItem.standardValue ?? ''}
                    onChange={(e) => setNewItem({ ...newItem, standardValue: e.target.value ? parseFloat(e.target.value) : null })}
                    placeholder="Standard value"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="error" className="text-xs text-gray-600">Tolerance</Label>
                  <Input
                    id="error"
                    type="number"
                    value={newItem.error ?? ''}
                    onChange={(e) => setNewItem({ ...newItem, error: e.target.value ? parseFloat(e.target.value) : null })}
                    placeholder="Tolerance (±)"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="unit" className="text-xs text-gray-600">Unit</Label>
                  <Input
                    id="unit"
                    value={newItem.unit}
                    onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                    placeholder="e.g. %"
                    className="mt-1"
                  />
                </div>
                <Button onClick={handleAddItem} variant="outline" className="border-blue-300 text-blue-600 hover:bg-blue-50">
                  <Plus className="size-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>

            {formData.items.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Parameter</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Standard</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Tolerance</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Min</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Max</th>
                      <th className="px-4 py-3 text-left text-xs text-gray-600 uppercase tracking-wider">Unit</th>
                      <th className="px-4 py-3 text-center text-xs text-gray-600 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {formData.items.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{item.parameter}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.standardValue ?? '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.error ?? '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.minValue ?? '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.maxValue ?? '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.unit}</td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveItem(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

            <div className="flex gap-2 pt-4 border-t">
            <Button onClick={handleSave} disabled={saving} variant="outline" className="bg-white text-gray-700 hover:bg-gray-50 border-gray-300">
              <Save className="size-4 mr-2" />
              {saving ? 'Saving...' : 'Save Specification'}
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              <X className="size-4 mr-2" />
              Cancel
            </Button>
          </div>
        </div>
      )}

      {loadingList ? (
        <div className="text-center py-8">Loading...</div>
      ) : specs.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Material Info
                  </th>
                  <th className="px-6 py-4 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Test Items
                  </th>
                  <th className="px-6 py-4 text-left text-xs text-gray-600 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-4 text-right text-xs text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {specs.map((spec) => (
                  <tr key={spec.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-900">{spec.materialName}</span>
                        <span className="text-xs text-gray-500">{spec.materialCode}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {spec.items.map((item, idx) => (
                          <div key={idx} className="text-sm text-gray-600">
                            <span className="text-gray-900">{item.parameter}</span>: {' '}
                            <span className="text-gray-700">{item.standardValue != null ? `${item.standardValue}` : '-'}</span>
                            <span className="text-gray-500"> {item.error != null ? ` ± ${item.error}` : ''}</span>
                            {' '}<span className="text-gray-400">（{item.minValue != null || item.maxValue != null ?
                              (item.minValue != null && item.maxValue != null ? `${item.minValue}-${item.maxValue}` : item.minValue != null ? `≥${item.minValue}` : `≤${item.maxValue}`)
                              : '-'}）</span>
                            {' '}<span className="text-gray-500">{item.unit}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {new Date(spec.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(spec)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(spec.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-slate-50">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span>Rows per page:</span>
                <select
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(parseInt(e.target.value, 10));
                    setPage(1);
                  }}
                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <span>
                {totalCount === 0 ? 'No data' : `${(page - 1) * perPage + 1}-${Math.min(page * perPage, totalCount)} of ${totalCount}`}
              </span>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1 || loadingList}
                className="p-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Previous Page"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div className="flex items-center h-8 px-2 mx-1">
                {Array.from({ length: Math.max(1, Math.ceil(totalCount / perPage)) }, (_, i) => i + 1).map(pageNum => {
                  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
                  const showPages = Math.min(5, totalPages);
                  const startPage = Math.max(1, Math.min(page - Math.floor(showPages / 2), totalPages - showPages + 1));
                  const endPage = Math.min(totalPages, startPage + showPages - 1);

                  if (pageNum < startPage || pageNum > endPage) return null;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`min-w-8 h-8 px-0.5 rounded text-sm transition-colors ${
                        page === pageNum ? 'bg-blue-600 text-white' : 'hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setPage(Math.min(Math.max(1, Math.ceil(totalCount / perPage)), page + 1))}
                disabled={page === Math.max(1, Math.ceil(totalCount / perPage)) || loadingList}
                className="p-1 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Next Page"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      ) : (
        !isAdding && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <FileText className="size-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg text-gray-600 mb-2">No specifications yet</h3>
            <p className="text-sm text-gray-400 mb-6">Click the button above to add your first specification</p>
            <Button onClick={() => setIsAdding(true)} variant="outline">
              <Plus className="size-4 mr-2" />
              New Specification
            </Button>
          </div>
        )
      )}
    </div>
  );
}